import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, downloadText } from "../lib/api";
import { AIGenerator } from "../components/AIGenerator";
import { ImageStudio } from "../components/ImageStudio";
import { useConfirm } from "../components/useConfirm";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Copy, Download, FileText, Image, Video, Filter, TrendingUp, ImagePlus } from "lucide-react";

const CATS = [
  { id: "testo", label: "Testo", icon: FileText },
  { id: "immagini", label: "Immagini", icon: Image },
  { id: "video", label: "Video", icon: Video },
];

export function AICourse() {
  const [imgPrompt, setImgPrompt] = useState("");
  const useForImage = (t) => { setImgPrompt(t); toast.success("Prompt inserito nel generatore immagini qui sotto"); };
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="ai-view">
      <FunnelPanel />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <PromptsPanel onUseForImage={useForImage} />
        <AIGenerator kind="ai_tutorials" sector="ai"
          title="Idee: micro-tutorial How-To"
          subtitle="Dimostrazioni pratiche sull'uso dell'AI per professionisti e creator"
          placeholder="Es. come scrivere una email con l'AI…" />
      </div>
      <ImageStudio prompt={imgPrompt} setPrompt={setImgPrompt} sector="ai" />
    </div>
  );
}

function FunnelPanel() {
  const [f, setF] = useState({ free_leads: 0, subscribers: 0, paying: 0, conversion_rate: 0, paying_rate: 0 });
  const load = () => api.get("/funnel").then((r) => setF(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const r = await api.put("/funnel", { free_leads: Number(f.free_leads) || 0, subscribers: Number(f.subscribers) || 0, paying: Number(f.paying) || 0 });
    setF(r.data); toast.success("Funnel aggiornato");
  };

  const Field = ({ label, k, color }) => (
    <div className="glass rounded-2xl p-5 text-center" style={{ borderTop: `3px solid ${color}` }}>
      <input data-testid={`funnel-${k}`} type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
        className="w-full bg-transparent text-center font-display font-extrabold text-3xl focus:outline-none font-mono-nums" />
      <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">{label}</p>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-violet-400" /><h3 className="font-display font-bold">Funnel di acquisizione</h3></div>
        <button data-testid="save-funnel-button" onClick={save} className="text-sm px-4 py-2 rounded-xl bg-violet-500 text-white font-semibold hover:bg-violet-400 transition-colors">Salva</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Field label="Lead gratuiti" k="free_leads" color="#8B5CF6" />
        <Field label="Iscritti percorso" k="subscribers" color="#8B5CF6" />
        <Field label="Paganti" k="paying" color="#8B5CF6" />
        <div className="glass rounded-2xl p-5 text-center bg-violet-500/5">
          <p className="font-display font-extrabold text-3xl font-mono-nums text-violet-300">{f.conversion_rate}%</p>
          <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">Conv. lead→iscritti</p>
        </div>
        <div className="glass rounded-2xl p-5 text-center bg-violet-500/5">
          <p className="font-display font-extrabold text-3xl font-mono-nums text-violet-300">{f.paying_rate}%</p>
          <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">Conv. iscritti→paganti</p>
        </div>
      </div>
    </div>
  );
}

function PromptsPanel({ onUseForImage }) {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", category: "testo", text: "" });
  const { confirm, ConfirmUI } = useConfirm();

  const load = () => api.get("/prompts").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const shown = filter === "all" ? list : list.filter((p) => p.category === filter);
  const openNew = () => { setEditing(null); setForm({ title: "", category: "testo", text: "" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };
  const save = async () => {
    if (!form.title.trim() || !form.text.trim()) { toast.error("Compila titolo e prompt"); return; }
    if (editing) await api.put(`/prompts/${editing.id}`, form); else await api.post("/prompts", form);
    toast.success("Prompt salvato"); setOpen(false); load();
  };
  const remove = async (p) => {
    if (!(await confirm({ title: "Eliminare il prompt?", description: p.title, confirmLabel: "Elimina", variant: "danger" }))) return;
    await api.delete(`/prompts/${p.id}`); toast.success("Eliminato"); load();
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Prompt copiato"); };
  const exportAll = () => {
    const md = shown.map((p) => `## ${p.title} (${p.category})\n\n${p.text}\n`).join("\n---\n\n");
    downloadText("prompt-ai.md", `# Database Prompt — AI allo Specchio\n\n${md}`);
  };

  return (
    <div className="glass rounded-2xl p-5" style={{ borderTop: "3px solid #8B5CF6" }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Filter className="h-5 w-5 text-violet-400" /><h3 className="font-display font-bold">Database prompt</h3></div>
        <div className="flex gap-2">
          <button data-testid="export-prompts-button" onClick={exportAll} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"><Download className="h-4 w-4" /> <span className="hidden sm:inline">Esporta</span></button>
          <button data-testid="new-prompt-button" onClick={openNew} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl bg-violet-500 text-white font-semibold hover:bg-violet-400 transition-colors"><Plus className="h-4 w-4" /> Nuovo</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <Chip id="all" filter={filter} setFilter={setFilter} label="Tutti" />
        {CATS.map((c) => <Chip key={c.id} id={c.id} filter={filter} setFilter={setFilter} label={c.label} icon={c.icon} />)}
      </div>
      <div className="space-y-2.5">
        {shown.length === 0 && <p className="text-sm text-slate-500">Nessun prompt in questa categoria.</p>}
        {shown.map((p) => {
          const cat = CATS.find((c) => c.id === p.category); const Icon = cat?.icon || FileText;
          return (
            <div key={p.id} className="p-3 rounded-xl bg-white/[0.03] border border-white/5 group" data-testid={`prompt-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5 text-violet-300" />
                    <p className="font-semibold text-sm">{p.title}</p>
                  </div>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{p.text}</p>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button data-testid={`copy-prompt-${p.id}`} onClick={() => copy(p.text)} className="p-1.5 rounded-lg hover:bg-violet-500/20"><Copy className="h-3.5 w-3.5 text-violet-300" /></button>
                  {p.category === "immagini" && onUseForImage && (
                    <button data-testid={`use-image-prompt-${p.id}`} onClick={() => onUseForImage(p.text)} className="p-1.5 rounded-lg hover:bg-fuchsia-500/20" title="Usa per generare immagine"><ImagePlus className="h-3.5 w-3.5 text-fuchsia-300" /></button>
                  )}
                  <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"><Pencil className="h-3.5 w-3.5 text-slate-400" /></button>
                  <button onClick={() => remove(p)} className="p-1.5 rounded-lg hover:bg-rose-500/20 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-3.5 w-3.5 text-rose-400" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-white/10 max-w-md" data-testid="prompt-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifica prompt" : "Nuovo prompt"}</DialogTitle>
            <DialogDescription className="text-slate-400">Salva un prompt pronto all'uso.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <input data-testid="prompt-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titolo del prompt" className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none" />
            <div>
              <label className="text-xs text-slate-400">Categoria</label>
              <select data-testid="prompt-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none">
                {CATS.map((c) => <option key={c.id} value={c.id} className="bg-slate-900">{c.label}</option>)}
              </select>
            </div>
            <textarea data-testid="prompt-text-input" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Testo del prompt…" rows={5} className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none resize-none" />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm hover:bg-white/10">Annulla</button>
            <button data-testid="save-prompt-button" onClick={save} className="px-4 py-2.5 rounded-xl bg-violet-500 text-white text-sm font-semibold hover:bg-violet-400">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {ConfirmUI}
    </div>
  );
}

function Chip({ id, filter, setFilter, label, icon: Icon }) {
  const on = filter === id;
  return (
    <button data-testid={`prompt-filter-${id}`} onClick={() => setFilter(id)}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
      style={on ? { background: "rgba(139,92,246,0.18)", color: "#8B5CF6", borderColor: "rgba(139,92,246,0.4)" } : { color: "#94a3b8", borderColor: "rgba(255,255,255,0.08)" }}>
      {Icon && <Icon className="h-3.5 w-3.5" />} {label}
    </button>
  );
}
